import type { AppRole } from '../roles';

// The menus only decide what is shown. Every action is still authorised by the broker.

export type MenuItemId =
  | 'home'
  | 'myRequests'
  | 'pettyCash'
  | 'review'
  | 'queue'
  | 'poApprovals'
  | 'purchaseOrders'
  | 'payments'
  | 'suppliers'
  | 'contracts'
  | 'dashboard'
  | 'admin'
  | 'audit';

export interface MenuItem {
  id: MenuItemId;
  path: string;
}

export interface Menus {
  /** Top bar. */
  top: MenuItem[];
  /** Avatar menu, above Sign out. */
  account: MenuItem[];
}

const ORDER: readonly MenuItem[] = [
  { id: 'home', path: '/' },
  { id: 'myRequests', path: '/requests' },
  { id: 'review', path: '/review' },
  { id: 'queue', path: '/queue' },
  { id: 'poApprovals', path: '/po-approvals' },
  { id: 'purchaseOrders', path: '/purchase-orders' },
  { id: 'pettyCash', path: '/petty-cash' },
  { id: 'payments', path: '/payments' },
  { id: 'suppliers', path: '/suppliers' },
  { id: 'contracts', path: '/contracts' },
  { id: 'dashboard', path: '/dashboard' },
  { id: 'admin', path: '/admin' },
  { id: 'audit', path: '/audit' },
];

// Every signed-in person is also a requester (01-SPEC §2). Petty cash is not theirs (D65).
const PERSONAL: readonly MenuItemId[] = ['myRequests'];

// Opened now and then, not daily: in the avatar menu when the top bar is long.
const PROCUREMENT_OVERFLOW: readonly MenuItemId[] = ['contracts', 'dashboard'];
const LATE_OVERFLOW: readonly MenuItemId[] = ['audit', 'admin'];
const MAX_TOP = 8;

// Dashboard for Admin, Procurement, MgmtViewer and the CFO (01-SPEC §7, D101); Finance has none
// in v1. The CFO also gets the purchase orders register (D101).
const WORK: Record<AppRole, readonly MenuItemId[]> = {
  Procurement: [
    'review',
    'queue',
    'purchaseOrders',
    'pettyCash',
    'payments',
    'suppliers',
    'contracts',
    'dashboard',
  ],
  CFO: ['poApprovals', 'purchaseOrders', 'dashboard'],
  // Petty cash is Procurement's route for small buys (D65); Finance decides the claims.
  Finance: ['pettyCash', 'payments'],
  Admin: ['dashboard', 'admin', 'audit'],
  MgmtViewer: ['dashboard'],
};

function pick(ids: ReadonlySet<MenuItemId>): MenuItem[] {
  return ORDER.filter((item) => ids.has(item.id));
}

/** `poApprover`: approves a vertical's POs after the CFO (D114), so sees PO approvals. */
export function menuFor(roles: readonly AppRole[], poApprover = false): Menus {
  const known = [...new Set(roles.filter((r): r is AppRole => Object.hasOwn(WORK, r)))];
  const work = new Set<MenuItemId>(known.flatMap((r) => WORK[r]));
  if (poApprover) work.add('poApprovals');

  // Long work menus (Procurement, or more than one role) move the personal items to the
  // avatar menu so the top bar stays on one line (09-DECISIONS-LOG D16).
  const long = known.includes('Procurement') || known.length > 1;
  if (long) {
    // Less frequent pages join them there too, so the bar stays readable (self-review C5):
    // Contracts and Dashboard for Procurement, then Audit and Admin while it is still too long.
    const moved: MenuItemId[] = [];
    const move = (id: MenuItemId) => {
      if (work.delete(id)) moved.push(id);
    };
    if (known.includes('Procurement')) PROCUREMENT_OVERFLOW.forEach(move);
    for (const id of LATE_OVERFLOW) if (work.size + 1 > MAX_TOP) move(id);
    return {
      top: pick(new Set(['home', ...work])),
      account: pick(new Set([...PERSONAL, ...moved])),
    };
  }
  return { top: pick(new Set(['home', ...PERSONAL, ...work])), account: [] };
}
